import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { motion, AnimatePresence } from 'framer-motion';
import { FaQuoteLeft, FaArrowLeft, FaArrowRight } from 'react-icons/fa';

const Testimonials = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  const testimonials = [
    {
      id: 1,
      quote: "Working with Samantha has been transformative. Her ability to ask the right questions helped me gain clarity on my leadership style and make meaningful changes.",
      name: "Alex Thompson",
      title: "Marketing Director"
    },
    {
      id: 2,
      quote: "The team coaching sessions with Samantha helped us improve communication and work more effectively together. Her approach is both professional and personable.",
      name: "Sarah Williams",
      title: "HR Manager"
    },
    {
      id: 3,
      quote: "Samantha's coaching came at a pivotal point in my career. Her guidance helped me navigate a challenging transition with confidence and purpose.",
      name: "Michael Chen",
      title: "Finance Executive"
    }
  ];
  
  const nextTestimonial = () => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };
  
  const prevTestimonial = () => {
    setActiveIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };
  
  useEffect(() => {
    if (!inView || isPaused) return;
    
    const interval = setInterval(() => {
      nextTestimonial();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [inView, isPaused, testimonials.length]);
  
  const testimonialVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.3 }
      }
    },
    exit: (direction) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0,
      transition: {
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.3 }
      }
    })
  };
  
  const [direction, setDirection] = useState(0);
  
  const handleNext = () => {
    setDirection(1);
    nextTestimonial();
  };
  
  const handlePrev = () => {
    setDirection(-1);
    prevTestimonial();
  };
  
  return (
    <TestimonialsContainer id="testimonials">
      <div className="container">
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
        >
          Client Testimonials
        </SectionTitle>
        
        <TestimonialsWrapper ref={ref}>
          <TestimonialControls>
            <ControlButton 
              onClick={handlePrev}
              disabled={testimonials.length <= 1}
              aria-label="Previous testimonial"
            >
              <FaArrowLeft />
            </ControlButton>
            
            <CarouselContainer
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <TestimonialCard
                  key={activeIndex}
                  custom={direction}
                  variants={testimonialVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.5 }}
                >
                  <QuoteWrapper>
                    <FaQuoteLeft />
                  </QuoteWrapper>
                  <QuoteText>
                    <p>{testimonials[activeIndex].quote}</p>
                  </QuoteText>
                  <ClientDetails>
                    <ClientName>{testimonials[activeIndex].name}</ClientName>
                    <ClientTitle>{testimonials[activeIndex].title}</ClientTitle>
                  </ClientDetails>
                </TestimonialCard>
              </AnimatePresence>
            </CarouselContainer>
            
            <ControlButton 
              onClick={handleNext}
              disabled={testimonials.length <= 1}
              aria-label="Next testimonial"
            >
              <FaArrowRight />
            </ControlButton>
          </TestimonialControls>
          
          <DotIndicators>
            {testimonials.map((_, index) => (
              <Dot 
                key={index} 
                active={activeIndex === index}
                onClick={() => {
                  setDirection(index > activeIndex ? 1 : -1);
                  setActiveIndex(index);
                }}
              />
            ))}
          </DotIndicators>
        </TestimonialsWrapper>
      </div>
      <BackgroundElement className="element-1" />
      <BackgroundElement className="element-2" />
      <DiagonalDivider />
    </TestimonialsContainer>
  );
};

const TestimonialsContainer = styled.section`
  background-color: #f9f9f9;
  position: relative;
  overflow: hidden;
  padding: 100px 0 120px;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 50px;
  position: relative;
  color: #1A2A3A;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: #A8C4A0;
  }
`;

const TestimonialsWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const TestimonialControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CarouselContainer = styled.div`
  flex: 1;
  position: relative;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TestimonialCard = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  position: relative;
  width: 80%;
  max-width: 700px;
  margin: 0 auto;
  overflow: hidden;
`;

const QuoteWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(168, 196, 160, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #5A7F8C;
  margin-bottom: 20px;
`;

const QuoteText = styled.div`
  p {
    font-size: 1.15rem;
    line-height: 1.7;
    color: #555;
    font-style: italic;
    position: relative;
    
    &:before, &:after {
      content: '';
      position: absolute;
      width: 20px;
      height: 2px;
      background-color: rgba(90, 127, 140, 0.2);
    }
    
    &:before {
      top: -10px;
      left: 0;
    }
    
    &:after {
      bottom: -10px;
      right: 0;
    }
  }
`;

const ClientDetails = styled.div`
  margin-top: 30px;
  text-align: right;
  position: relative;
  padding-right: 15px;
  
  &:after {
    content: '';
    position: absolute;
    top: 10px;
    right: 0;
    height: 70%;
    width: 3px;
    background-color: #A8C4A0;
  }
`;

const ClientName = styled.h4`
  font-size: 1.2rem;
  color: #1A2A3A;
  margin-bottom: 5px;
`;

const ClientTitle = styled.p`
  font-size: 0.9rem;
  color: #5A7F8C;
`;

const ControlButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: white;
  border: none;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #5A7F8C;
  transition: all 0.3s ease;
  z-index: 3;
  
  &:hover:not(:disabled) {
    background-color: #5A7F8C;
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 576px) {
    width: 40px;
    height: 40px;
    font-size: 0.8rem;
  }
`;

const DotIndicators = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#A8C4A0' : '#ddd'};
  margin: 0 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.2);
  }
`;

const BackgroundElement = styled.div`
  position: absolute;
  
  &.element-1 {
    width: 600px;
    height: 600px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v50H50v50H0z' fill='%23A8C4A0' fill-opacity='0.05'/%3E%3C/svg%3E");
    top: -200px;
    right: -200px;
    z-index: 0;
    transform: rotate(-15deg);
  }
  
  &.element-2 {
    width: 500px;
    height: 500px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h50v50H0z' fill='%235A7F8C' fill-opacity='0.03'/%3E%3C/svg%3E");
    bottom: -100px;
    left: -150px;
    z-index: 0;
    transform: rotate(10deg);
  }
`;

const DiagonalDivider = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background-color: white;
  clip-path: polygon(0 100%, 100% 40%, 100% 100%, 0 100%);
  z-index: 1;
`;

export default Testimonials;